import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Flight } from '../../types/Quotation';
import AsyncSelect from 'react-select/async';
import { LocationOption } from '../../types/Location';
import { loadAirportLocations } from '../../api/Location';
import { useFlights } from '../../hooks/useFlights';
import RoomsTravellersDropdown from '../Common/RoomsTravellersDropdown';
import { QuotationRoomOccupancy } from './quotationRoomOccupancy';
import '../../css/FlightListModal.css';

export type FlightSearchContext = {
  from?: LocationOption;
  to?: LocationOption;
  departureDate: string;
  returnDate?: string;
  isRoundTrip: boolean;
};

interface FlightListModalProps {
  startDate: string;
  from?: LocationOption;
  to?: LocationOption;
  travellers?: { adults: number; children: number; infants: number };
  rooms?: QuotationRoomOccupancy[];
  onRoomsChange?: (rooms: QuotationRoomOccupancy[]) => void;
  // Prefill helpers
  roundTripDefault?: boolean;
  disableOneWay?: boolean;
  disableRoundTrip?: boolean;
  returnDate?: string;
  defaultFromCityLabel?: string;
  defaultToCityLabel?: string; // e.g., first destination city like "Bangkok"
  onClose: () => void;
  onSelectOutbound: (flight: Flight) => void;
  onSelectReturn: (flight: Flight) => void;
  embedded?: boolean;
  onSelectionContext?: (context: FlightSearchContext) => void;
}

const getFlightKey = (f: Flight) =>
  `${f.provider || ''}-${f.flightNumber}-${f.departureTime}-${f.arrivalTime}-${f.price}`;

const fmtTime = (dt: string) =>
  new Date(dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const stopsLabel = (stops: number) => {
  if (stops <= 0) return 'Non-stop';
  if (stops === 1) return '1 stop';
  return `${stops} stops`;
};

const getCodeFromLabel = (label?: string) => {
  if (!label) return '';
  const m = /\(([A-Z0-9]{3})\)/.exec(label);
  return (m?.[1] || '').trim();
};

type AirportLocationOption = LocationOption & {
  airportName?: string;
  airport_name?: string;
  cityName?: string;
  city_name?: string;
  iataCode?: string;
  iata_code?: string;
  name?: string;
};

const airportOptionDetails = (option: LocationOption) => {
  const airport = option as AirportLocationOption;
  const code = airport.iataCode || airport.iata_code || getCodeFromLabel(option.label) || option.value;
  const city = airport.cityName || airport.city_name;
  const airportName = airport.airportName || airport.airport_name || airport.name;
  const fallbackName = option.label.replace(/\s*\([A-Z0-9]{3}\)\s*$/, '').trim();

  return {
    code,
    details: [city, airportName || fallbackName].filter(Boolean).join(' · '),
  };
};

const airportOptionSearchLabel = (option: LocationOption) => {
  const airport = option as AirportLocationOption;
  return [
    option.label,
    option.value,
    airport.iataCode,
    airport.iata_code,
    airport.cityName,
    airport.city_name,
    airport.airportName,
    airport.airport_name,
    airport.name,
  ].filter(Boolean).join(' ');
};

const AirportOptionLabel: React.FC<{ option: LocationOption }> = ({ option }) => {
  const { code, details } = airportOptionDetails(option);
  return (
    <div className="flm-airportOption">
      <strong>{code}</strong>
      {details && <span>{details}</span>}
    </div>
  );
};

type AnySegment = {
  fromCode?: string;
  toCode?: string;
  origin?: string;
  destination?: string;
  departureTime?: string;
  arrivalTime?: string;
  durationMins?: number;
  airlineCode?: string;
  airline?: string;
  flightNumber?: string;
};

const segmentCode = (s: AnySegment, side: 'from' | 'to') => {
  if (side === 'from') return (s.fromCode || s.origin || '').toString();
  return (s.toCode || s.destination || '').toString();
};

const formatDurationMins = (mins: number) => {
  if (!Number.isFinite(mins) || mins <= 0) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h${m ? ` ${m}m` : ''}`;
};

const FlightCard: React.FC<{
  flight: Flight;
  selected: boolean;
  disabled?: boolean;
  actionLabel: string;
  fromCodeHint?: string;
  toCodeHint?: string;
  onSelect: (flight: Flight) => void;
}> = ({ flight, selected, disabled, actionLabel, fromCodeHint, toCodeHint, onSelect }) => {
  const segments: AnySegment[] = Array.isArray((flight as any).segments) ? (flight as any).segments : [];
  const fromCode = flight.from || fromCodeHint || (segments[0] ? segmentCode(segments[0], 'from') : '');
  const toCode =
    flight.to || toCodeHint || (segments.length ? segmentCode(segments[segments.length - 1], 'to') : '');

  const badge = flight.baggage || 'Cabin baggage included';

  return (
    <div className={`flm-card ${selected ? 'is-selected' : ''}`}>
      <div className="flm-cardMain">
        <div className="flm-cardTop">
          <div className="flm-airline">
            <img
              src={`https://cdn.otaprime.com/airline/${flight.provider}.gif`}
              alt={flight.airline}
              className="flm-airlineLogo"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
              }}
            />
            <div className="flm-airlineText">
              <div className="flm-airlineName">{flight.airline}</div>
              <div className="flm-flightNo">{flight.provider}-{flight.flightNumber}</div>
            </div>
          </div>

          <div className="flm-meta">
            <span className="flm-chip">{stopsLabel(flight.stops)}</span>
            {flight.duration && <span className="flm-chip flm-chipMuted">{flight.duration}</span>}
          </div>
        </div>

        <div className="flm-cardMid">
          <div className="flm-timeBlock">
            <div className="flm-time">{fmtTime(flight.departureTime)}</div>
            <div className="flm-code">{fromCode}</div>
          </div>

          <div className="flm-line">
            <div className="flm-lineBar" />
            <div className="flm-lineIcon">✈︎</div>
          </div>

          <div className="flm-timeBlock">
            <div className="flm-time">{fmtTime(flight.arrivalTime)}</div>
            <div className="flm-code">{toCode}</div>
          </div>
        </div>

        <div className="flm-cardBottom">
          <span className="flm-badge">{badge}</span>

          {segments.length > 0 && (
            <details className="flm-details">
              <summary>Details</summary>
              <div className="flm-detailsBody">
                <div className="flm-detailsTitle">Segments</div>
                <ul className="flm-segList">
                  {segments.map((s, idx) => {
                    const segFrom = segmentCode(s, 'from');
                    const segTo = segmentCode(s, 'to');
                    const segDur = typeof s.durationMins === 'number' ? formatDurationMins(s.durationMins) : '';
                    const segNo = `${s.airlineCode || flight.provider || ''}-${s.flightNumber || ''}`.replace(/-$/, '');
                    return (
                      <li className="flm-segItem" key={`${segFrom}-${segTo}-${idx}`}>
                        <div className="flm-segMain">
                          <span className="flm-segRoute">
                            {segFrom} → {segTo}
                          </span>
                          {segNo && <span className="flm-segNo">{segNo}</span>}
                        </div>
                        <div className="flm-segSub">
                          {s.departureTime && s.arrivalTime && (
                            <span>
                              {fmtTime(s.departureTime)}–{fmtTime(s.arrivalTime)}
                            </span>
                          )}
                          {segDur && <span>• {segDur}</span>}
                        </div>

                        {idx < segments.length - 1 && s.arrivalTime && segments[idx + 1]?.departureTime && (
                          <div className="flm-layover">
                            Layover in {segTo}:{' '}
                            {formatDurationMins(
                              Math.max(
                                0,
                                Math.round(
                                  (+new Date(segments[idx + 1].departureTime as string) - +new Date(s.arrivalTime)) /
                                    60000
                                )
                              )
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </details>
          )}
        </div>
      </div>

      <div className="flm-cardSide">
        <div className="flm-price">₹{flight.price.toLocaleString('en-IN')}</div>
        <button
          type="button"
          className="flm-selectBtn"
          disabled={disabled}
          onClick={() => onSelect(flight)}
        >
          {selected ? 'Selected' : actionLabel}
        </button>
      </div>
    </div>
  );
};

const FlightListModal: React.FC<FlightListModalProps> = ({
  startDate,
  from: defaultFrom,
  to: defaultTo,
  travellers,
  rooms,
  onRoomsChange,
  roundTripDefault,
  disableOneWay,
  disableRoundTrip,
  returnDate,
  defaultFromCityLabel,
  defaultToCityLabel,
  onClose,
  onSelectOutbound,
  onSelectReturn,
  embedded = false,
  onSelectionContext,
}) => {
  const resolveInitialIsRoundTrip = () => {
    if (disableOneWay) return true;
    if (disableRoundTrip) return false;
    return !!roundTripDefault;
  };

  // Form state (what the user is editing)
  const [formFromOption, setFormFromOption] = useState<LocationOption | undefined>(defaultFrom);
  const [formToOption, setFormToOption] = useState<LocationOption | undefined>(defaultTo);
  const [formDepDate, setFormDepDate] = useState(startDate);
  const [formIsRoundTrip, setFormIsRoundTrip] = useState<boolean>(resolveInitialIsRoundTrip);
  const [formRetDate, setFormRetDate] = useState(returnDate || startDate);
  const [formCabinClass, setFormCabinClass] = useState<number>(1);

  // Query state (what is currently being searched)
  const [queryFromOption, setQueryFromOption] = useState<LocationOption | undefined>(defaultFrom);
  const [queryToOption, setQueryToOption] = useState<LocationOption | undefined>(defaultTo);
  const [queryDepDate, setQueryDepDate] = useState(startDate);
  const [queryIsRoundTrip, setQueryIsRoundTrip] = useState<boolean>(resolveInitialIsRoundTrip);
  const [queryRetDate, setQueryRetDate] = useState(returnDate || startDate);
  const [queryCabinClass, setQueryCabinClass] = useState<number>(1);

  const [sortBy, setSortBy] = useState<'price' | 'departure' | 'arrival' | 'duration'>('price');
  const [stopsFilter, setStopsFilter] = useState<'any' | '0' | '1' | '2+'>('any');
  const [selectedOutboundKey, setSelectedOutboundKey] = useState<string | null>(null);
  const [selectedReturnKey, setSelectedReturnKey] = useState<string | null>(null);
  const [activeLeg, setActiveLeg] = useState<'outbound' | 'return'>('outbound');

  useEffect(() => {
    setFormDepDate(startDate);
    setQueryDepDate(startDate);
  }, [startDate]);

  useEffect(() => {
    if (!returnDate) return;
    setFormRetDate(returnDate);
    setQueryRetDate(returnDate);
  }, [returnDate]);

  useEffect(() => {
    if (disableOneWay) {
      setFormIsRoundTrip(true);
      setQueryIsRoundTrip(true);
      return;
    }
    if (disableRoundTrip) {
      setFormIsRoundTrip(false);
      setQueryIsRoundTrip(false);
    }
  }, [disableOneWay, disableRoundTrip]);

  // Attempt to prefill destination airport based on a city label when not provided
  useEffect(() => {
    let cancelled = false;
    if (!formFromOption && defaultFromCityLabel && defaultFromCityLabel.trim().length >= 3) {
      loadAirportLocations(defaultFromCityLabel).then((opts) => {
        if (cancelled) return;
        const lower = defaultFromCityLabel.toLowerCase();
        const match = (opts || []).find((o) => o.label.toLowerCase().includes(lower)) || (opts || [])[0];
        if (!match) return;
        setFormFromOption(match);
        setQueryFromOption((prev) => prev || match);
      }).catch(() => void 0);
    }
    if (!formToOption && defaultToCityLabel && defaultToCityLabel.trim().length >= 3) {
      loadAirportLocations(defaultToCityLabel).then((opts) => {
        if (cancelled) return;
        const lower = defaultToCityLabel.toLowerCase();
        const match = (opts || []).find(o => o.label.toLowerCase().includes(lower)) || (opts || [])[0];
        if (!match) return;
        setFormToOption(match);
        // If user hasn't searched yet (or query is empty), keep query in sync too.
        setQueryToOption(prev => prev || match);
      }).catch(() => void 0);
    }
    return () => { cancelled = true; };
  }, [defaultFromCityLabel, defaultToCityLabel, formFromOption, formToOption]);

  const {
    flights,
    loading,
    error,
  } = useFlights(
    queryFromOption ? queryFromOption.value : '',
    queryToOption ? queryToOption.value : '',
    queryDepDate,
    travellers,
    queryCabinClass
  );

  const {
    flights: returnFlights,
    loading: loadingReturn,
    error: errorReturn,
  } = useFlights(
    queryIsRoundTrip ? (queryToOption ? queryToOption.value : '') : '',
    queryIsRoundTrip ? (queryFromOption ? queryFromOption.value : '') : '',
    queryIsRoundTrip ? queryRetDate : '',
    travellers,
    queryCabinClass
  );

  const filterByStops = useCallback((arr: Flight[]) => arr.filter(f => {
    if (stopsFilter === 'any') return true;
    if (stopsFilter === '0') return f.stops === 0;
    if (stopsFilter === '1') return f.stops === 1;
    return f.stops >= 2;
  }), [stopsFilter]);

  const parseDur = (d: string) => {
    const h = /([0-9]+)h/.exec(d)?.[1];
    const m = /([0-9]+)m/.exec(d)?.[1];
    return (h ? parseInt(h, 10) * 60 : 0) + (m ? parseInt(m, 10) : 0);
  };

  const sortFlights = useCallback((arr: Flight[]) => {
    switch (sortBy) {
      case 'price':
        return [...arr].sort((a, b) => a.price - b.price);
      case 'departure':
        return [...arr].sort((a, b) => +new Date(a.departureTime) - +new Date(b.departureTime));
      case 'arrival':
        return [...arr].sort((a, b) => +new Date(a.arrivalTime) - +new Date(b.arrivalTime));
      case 'duration':
        return [...arr].sort((a, b) => parseDur(a.duration) - parseDur(b.duration));
      default:
        return arr;
    }
  }, [sortBy]);

  const visibleFlights = useMemo(
    () => sortFlights(filterByStops(flights)),
    [flights, filterByStops, sortFlights]
  );
  
  const visibleReturnFlights = useMemo(() => {
    if (!queryIsRoundTrip) return [] as typeof flights;
    return sortFlights(filterByStops(returnFlights));
  }, [returnFlights, queryIsRoundTrip, filterByStops, sortFlights]);

  const canSearch =
    !!formFromOption?.value &&
    !!formToOption?.value &&
    !!formDepDate &&
    (!formIsRoundTrip || !!formRetDate);

  const applySearch = () => {
    if (!canSearch) return;
    setQueryFromOption(formFromOption);
    setQueryToOption(formToOption);
    setQueryDepDate(formDepDate);
    setQueryIsRoundTrip(formIsRoundTrip);
    setQueryRetDate(formRetDate);
    setQueryCabinClass(formCabinClass);
    setSelectedOutboundKey(null);
    setSelectedReturnKey(null);
    setActiveLeg('outbound');
  };

  const fromCodeHint = getCodeFromLabel(queryFromOption?.label) || queryFromOption?.value || '';
  const toCodeHint = getCodeFromLabel(queryToOption?.label) || queryToOption?.value || '';

  const selectionContext = (): FlightSearchContext => ({
    from: queryFromOption,
    to: queryToOption,
    departureDate: queryDepDate,
    returnDate: queryIsRoundTrip ? queryRetDate : undefined,
    isRoundTrip: queryIsRoundTrip,
  });

  const content = (
      <div className="flightListModal" role="dialog" aria-modal={!embedded} aria-label="Select flights">
        <div className="flm-header">
          <div className="flm-title">
            <div className="flm-titleMain">
              <span className="flm-route">
                {(fromCodeHint || 'From')} <span className="flm-arrow">→</span> {(toCodeHint || 'To')}
              </span>
              <span className="flm-tripType">{queryIsRoundTrip ? 'Round trip' : 'One way'}</span>
            </div>
            <div className="flm-titleSub">
              {queryDepDate}
              {queryIsRoundTrip ? ` • return ${queryRetDate}` : ''}
              {travellers ? ` • ${travellers.adults}A ${travellers.children}C ${travellers.infants}I` : ''}
            </div>
          </div>

          {!embedded && <button type="button" className="flm-close" onClick={onClose} aria-label="Close">
            ×
          </button>}
        </div>

        <div className="flm-controls">
          <div className="flm-formGrid">
            <div className="flm-field">
              <div className="flm-label">Trip</div>
              <div className="flm-seg">
                <button
                  type="button"
                  className={`flm-segBtn ${!formIsRoundTrip ? 'is-active' : ''}`}
                  disabled={!!disableOneWay}
                  onClick={() => setFormIsRoundTrip(false)}
                >
                  One way
                </button>
                <button
                  type="button"
                  className={`flm-segBtn ${formIsRoundTrip ? 'is-active' : ''}`}
                  disabled={!!disableRoundTrip}
                  onClick={() => setFormIsRoundTrip(true)}
                >
                  Round trip
                </button>
              </div>
            </div>

            <div className="flm-field">
              <div className="flm-label">From</div>
              <AsyncSelect<LocationOption, false>
                cacheOptions
                defaultOptions
                loadOptions={loadAirportLocations}
                getOptionLabel={airportOptionSearchLabel}
                getOptionValue={(o) => o.value}
                formatOptionLabel={(option) => <AirportOptionLabel option={option} />}
                onChange={(opt) => setFormFromOption(opt as LocationOption)}
                value={formFromOption}
                placeholder="Type or select an airport"
                classNamePrefix="flm-rs"
              />
            </div>

            <div className="flm-field">
              <div className="flm-label">To</div>
              <AsyncSelect<LocationOption, false>
                cacheOptions
                defaultOptions
                loadOptions={loadAirportLocations}
                getOptionLabel={airportOptionSearchLabel}
                getOptionValue={(o) => o.value}
                formatOptionLabel={(option) => <AirportOptionLabel option={option} />}
                onChange={(opt) => setFormToOption(opt as LocationOption)}
                value={formToOption}
                placeholder="Type or select an airport"
                classNamePrefix="flm-rs"
              />
            </div>

            <div className="flm-field">
              <div className="flm-label">Onward</div>
              <input type="date" value={formDepDate} onChange={(e) => setFormDepDate(e.target.value)} />
            </div>

            <div className="flm-field">
              <div className="flm-label">Return</div>
              <input
                type="date"
                value={formRetDate}
                onChange={(e) => setFormRetDate(e.target.value)}
                disabled={!formIsRoundTrip}
              />
            </div>

            <div className="flm-field">
              <div className="flm-label">Cabin</div>
              <select value={formCabinClass} onChange={(e) => setFormCabinClass(Number(e.target.value))}>
                <option value={1}>All</option>
                <option value={2}>Economy</option>
                <option value={3}>Premium Economy</option>
                <option value={4}>Business</option>
                <option value={5}>Premium Business</option>
                <option value={6}>First</option>
              </select>
            </div>

            {embedded && rooms && onRoomsChange && (
              <div className="flm-field flm-field--rooms">
                <div className="flm-label">Rooms & Travellers</div>
                <RoomsTravellersDropdown value={rooms} onChange={onRoomsChange} />
              </div>
            )}

            <div className="flm-actions">
              <button type="button" className="flm-primaryBtn" onClick={applySearch} disabled={!canSearch}>
                Search
              </button>
              {!canSearch && <div className="flm-help">Select From/To + dates to search</div>}
            </div>
          </div>

          <div className="flm-filters">
            <div className="flm-filterGroup">
              <div className="flm-filterLabel">Sort</div>
              <div className="flm-pillRow">
                <button
                  type="button"
                  className={`flm-pill ${sortBy === 'price' ? 'is-active' : ''}`}
                  onClick={() => setSortBy('price')}
                >
                  Price
                </button>
                <button
                  type="button"
                  className={`flm-pill ${sortBy === 'departure' ? 'is-active' : ''}`}
                  onClick={() => setSortBy('departure')}
                >
                  Departure
                </button>
                <button
                  type="button"
                  className={`flm-pill ${sortBy === 'arrival' ? 'is-active' : ''}`}
                  onClick={() => setSortBy('arrival')}
                >
                  Arrival
                </button>
                <button
                  type="button"
                  className={`flm-pill ${sortBy === 'duration' ? 'is-active' : ''}`}
                  onClick={() => setSortBy('duration')}
                >
                  Duration
                </button>
              </div>
            </div>

            <div className="flm-filterGroup">
              <div className="flm-filterLabel">Stops</div>
              <select value={stopsFilter} onChange={(e) => setStopsFilter(e.target.value as any)}>
                <option value="any">Any</option>
                <option value="0">Non-stop</option>
                <option value="1">1 stop</option>
                <option value="2+">2+ stops</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flm-results">
          {queryIsRoundTrip && (
            <div className="flm-tabs" role="tablist" aria-label="Select leg">
              <button
                type="button"
                role="tab"
                aria-selected={activeLeg === 'outbound'}
                className={`flm-tab ${activeLeg === 'outbound' ? 'is-active' : ''}`}
                onClick={() => setActiveLeg('outbound')}
              >
                <span>Outbound</span>
                <span className="flm-tabCount">{visibleFlights.length}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeLeg === 'return'}
                className={`flm-tab ${activeLeg === 'return' ? 'is-active' : ''}`}
                onClick={() => {
                  if (!selectedOutboundKey) return;
                  setActiveLeg('return');
                }}
                disabled={!selectedOutboundKey}
                title={!selectedOutboundKey ? 'Select an outbound flight first' : undefined}
              >
                <span>Return</span>
                <span className="flm-tabCount">{visibleReturnFlights.length}</span>
              </button>
            </div>
          )}

          <div className="flm-sectionHeader">
            <div className="flm-sectionTitle">{activeLeg === 'outbound' ? 'Outbound flights' : 'Return flights'}</div>
            <div className="flm-sectionMeta">
              {activeLeg === 'outbound'
                ? loading
                  ? 'Loading…'
                  : `${visibleFlights.length} results`
                : loadingReturn
                  ? 'Loading…'
                  : `${visibleReturnFlights.length} results`}
              {activeLeg === 'outbound' && error ? <span className="flm-error"> • Failed to load</span> : null}
              {activeLeg === 'return' && errorReturn ? <span className="flm-error"> • Failed to load</span> : null}
            </div>
          </div>

          {activeLeg === 'outbound' && (
            <>
              {loading && <div className="flm-empty">Searching flights…</div>}
              {!loading && visibleFlights.length === 0 && (
                <div className="flm-empty">No outbound flights found for the current search.</div>
              )}
              {!loading &&
                visibleFlights.map((f) => {
                  const key = getFlightKey(f);
                  return (
                    <FlightCard
                      key={key}
                      flight={f}
                      selected={selectedOutboundKey === key}
                      actionLabel="Select"
                      fromCodeHint={fromCodeHint}
                      toCodeHint={toCodeHint}
                      onSelect={(flight) => {
                        const k = getFlightKey(flight);
                        setSelectedOutboundKey(k);
                        setSelectedReturnKey(null);
                        onSelectOutbound({
                          ...flight,
                          from: queryFromOption?.label || flight.from,
                          to: queryToOption?.label || flight.to,
                        });
                        onSelectionContext?.(selectionContext());
                        if (queryIsRoundTrip) setActiveLeg('return');
                        else if (!embedded) onClose();
                      }}
                    />
                  );
                })}
            </>
          )}

          {activeLeg === 'return' && (
            <>
              {!selectedOutboundKey && (
                <div className="flm-empty flm-hint">Select an outbound flight to enable return selection.</div>
              )}
              {loadingReturn && <div className="flm-empty">Searching return flights…</div>}
              {!loadingReturn && selectedOutboundKey && visibleReturnFlights.length === 0 && (
                <div className="flm-empty">No return flights found for the current search.</div>
              )}
              {!loadingReturn &&
                visibleReturnFlights.map((f) => {
                  const key = getFlightKey(f);
                  return (
                    <FlightCard
                      key={`ret-${key}`}
                      flight={f}
                      selected={selectedReturnKey === key}
                      disabled={!selectedOutboundKey}
                      actionLabel="Select"
                      fromCodeHint={toCodeHint}
                      toCodeHint={fromCodeHint}
                      onSelect={(flight) => {
                        if (!selectedOutboundKey) return;
                        const k = getFlightKey(flight);
                        setSelectedReturnKey(k);
                        onSelectReturn({
                          ...flight,
                          from: queryToOption?.label || flight.from,
                          to: queryFromOption?.label || flight.to,
                        });
                        onSelectionContext?.(selectionContext());
                        if (!embedded) onClose();
                      }}
                    />
                  );
                })}
            </>
          )}
        </div>
      </div>
  );

  return embedded ? <div className="flightListEmbedded">{content}</div> : <div className="flightListModalBackdrop">{content}</div>;
};

export default FlightListModal;
